import { useEffect, useRef, type RefObject } from 'react';

// Elements Tab normally visits: links with a real target, non-disabled form
// controls/buttons, and anything explicitly opted into the tab order. A
// negative tabindex (`tabindex="-1"`, used for programmatically-focusable
// containers like the tour card) is excluded on purpose — that's how a
// component marks "focusable via .focus(), not via Tab".
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/** Focus the first focusable descendant, or the container itself as a last resort. */
function focusInto(container: HTMLElement) {
  const first = focusableElements(container)[0];
  if (first) {
    first.focus();
  } else {
    // Nothing tabbable inside yet — focus the container itself so Tab and
    // screen readers still have somewhere to land.
    container.setAttribute('tabindex', '-1');
    container.focus();
  }
}

interface TrapEntry {
  container: HTMLElement;
  previouslyFocused: HTMLElement | null;
}

/**
 * Every currently-active trap in the app, topmost (most recently opened)
 * last. More than one dialog CAN be genuinely open at once here — the
 * idle-timeout warning or the unsaved-changes prompt can appear over a page's
 * own modal — and without this shared stack, each trap's `document`-level Tab
 * listener and restore-on-close logic would act in total ignorance of the
 * other: both would fight to reclaim Tab every keystroke, and closing the
 * *lower* dialog (e.g. a background modal auto-dismissing) would yank focus
 * all the way back to the page instead of leaving it in the dialog still open
 * on top. Identity in the stack is by entry object, not array position, so
 * traps can close in any order (not just LIFO).
 */
const stack: TrapEntry[] = [];

/**
 * Traps Tab/Shift+Tab focus cycling within a dialog or overlay while `active`
 * is true (PS2): moves focus into the surface when it opens, keeps Tab from
 * escaping into the obscured page (or a lower dialog) behind it, and returns
 * focus once `active` goes false or the component unmounts — to the next
 * dialog down if one is still open, otherwise to whatever triggered this one.
 *
 * Attach the returned ref to the dialog's outermost element — the one
 * carrying `role="dialog"` / `role="alertdialog"`. Deliberately does NOT
 * handle Escape: every dialog in this app already has its own Escape
 * behavior (most close, the tour treats it as "skip the tour"), so that stays
 * local to each component and this hook only owns Tab.
 *
 * If a component wants a more specific initial focus target than "the first
 * focusable element" (a primary action, an input to select, the tour's
 * narration card), call that hook/effect AFTER `useFocusTrap` in the
 * component body — effects run in declaration order, so the later, more
 * specific `.focus()` call wins over this hook's generic fallback.
 */
export function useFocusTrap<T extends HTMLElement>(active: boolean): RefObject<T | null> {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const entry: TrapEntry = {
      container,
      previouslyFocused: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    };
    stack.push(entry);

    if (!entry.container.contains(document.activeElement)) focusInto(entry.container);

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      // Only the topmost trap owns Tab — a trap left mounted underneath a
      // later dialog (e.g. the idle-timeout warning firing over a page's own
      // modal) must not also try to reclaim focus into itself.
      if (stack[stack.length - 1] !== entry) return;

      const items = focusableElements(entry.container);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (e.shiftKey) {
        if (current === first || !entry.container.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !entry.container.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);

      const idx = stack.indexOf(entry);
      const wasTop = idx === stack.length - 1;
      if (idx !== -1) stack.splice(idx, 1);

      if (stack.length > 0) {
        // Another trap is still open. Only hand it focus if the one that just
        // closed was on top (so focus was plausibly inside it) — a lower trap
        // closing out of order (this one) must leave focus exactly where it
        // is, inside whatever dialog is still open above it.
        const newTop = stack[stack.length - 1];
        if (wasTop && !newTop.container.contains(document.activeElement)) {
          focusInto(newTop.container);
        }
      } else {
        const restore = entry.previouslyFocused;
        if (restore && document.body.contains(restore)) restore.focus();
      }
    };
  }, [active]);

  return containerRef;
}
